/**
 * One icon AND one colour per queue lifecycle action, everywhere.
 * For Media Devoted | Created By Ciro Autiero
 *
 * "Move to live" had drifted to THREE different glyphs — a rocket on the row
 * button, an arrow in the bulk bar, a play symbol on the eliminated row — and
 * "move to queue" to two. An operator learns an icon once and then reads it
 * across tabs, so a second glyph for the same verb is a bug in the interface,
 * not a style choice. Import from here rather than reaching for lucide directly.
 *
 * The colour is the same argument: every button that sends a row somewhere is
 * tinted with the colour that destination already wears as a status badge.
 */

import {
  ArrowDownCircle,
  Hourglass,
  ListPlus,
  Rocket,
  Trash2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';

/** Put an element live on the tracker now (row button, bulk bar, revive). */
export const IconMoveToLive: LucideIcon = Rocket;

/** Put an element into the queue line — a candidate for the next rotation. */
export const IconMoveToQueue: LucideIcon = ListPlus;

/** Park an element in Waiting: kept, but never promoted until moved. */
export const IconMoveToWaiting: LucideIcon = Hourglass;

/** Tombstone an element in the bank. */
export const IconEliminate: LucideIcon = Trash2;

/** Take a LIVE element off the tracker (the Live tab's demote). */
export const IconDemote: LucideIcon = ArrowDownCircle;

/** Revive a tombstoned element — the undo of Eliminate. */
export const IconBringBack: LucideIcon = Undo2;

/** The verb a button performs — what it will make the row, not where it sits. */
export type QueueActionTone = 'live' | 'queue' | 'waiting' | 'eliminate';

/**
 * Colour follows the DESTINATION's status badge (queue-status.util.ts), so the
 * button and the badge the row wears after you click it are the same colour:
 * green Live, amber Queued, grey Waiting — its badge tone is `secondary`, so
 * grey IS its theme colour — and red for the one destructive verb.
 *
 * Tinted, not filled: three solid buttons per row is a shout, and the row is
 * already carrying a name, a rule and a set of numbers. The label deliberately
 * stays `text-foreground` — amber text at 10.5px on a pale amber fill is close
 * to unreadable — so the border, the fill and the glyph carry the colour.
 */
const TONE_SURFACE: Record<QueueActionTone, string> = {
  live: 'border-success/40 bg-success/10 hover:border-success/70 hover:bg-success/20',
  queue: 'border-warning/50 bg-warning/10 hover:border-warning/80 hover:bg-warning/20',
  waiting: 'border-border bg-muted hover:border-foreground/25 hover:bg-muted/70',
  eliminate:
    'border-destructive/40 bg-destructive/10 hover:border-destructive/70 hover:bg-destructive/20',
};

const TONE_GLYPH: Record<QueueActionTone, string> = {
  live: 'text-success',
  queue: 'text-warning',
  waiting: 'text-muted-foreground',
  eliminate: 'text-destructive',
};

/** Border + fill for a toned action. Use on the shared `Button` component. */
export function queueActionSurface(tone: QueueActionTone): string {
  return TONE_SURFACE[tone];
}

/** Icon colour for a toned action — the glyph carries the colour, not the label. */
export function queueActionGlyph(tone: QueueActionTone): string {
  return TONE_GLYPH[tone];
}

/** Shape of the compact in-row action buttons (Live / Eliminated tables). */
const ROW_BTN =
  'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded border px-2 text-[10.5px] font-medium text-foreground transition-colors disabled:opacity-50';

/** A complete compact row button: shared shape, per-verb colour. */
export function queueActionButton(tone: QueueActionTone): string {
  return cn(ROW_BTN, TONE_SURFACE[tone]);
}
